import { FaultLevel, TimeRange } from '../slices/globalSlice';
import { AssetListScenarios, FaultStatus, GpsFix, SoftwareStatus, YesNo } from './assetsApi';
import { IgnitionMinimum } from './healthApi';
import { FiltersState } from '../slices/filtersSlice';

export enum QueryParamNames {
  AssetGroups = 'ags',
  AssetIds = 'assets',
  Audible = 'aud',
  ConfigGroups = 'cgs',
  DeviceTypes = 'hws',
  DistanceLessThan = 'dlt',
  DocumentType = 'documentType',
  DurationGreaterThan = 'tgt',
  EndLattitude = 'eLat',
  EndLongitude = 'eLon',
  FaultCategories = 'fcs',
  FaultLevel = 'fl',
  FaultLevels = 'fls',
  Faults = 'fids',
  FaultStatus = 'fr',
  GeofenceAreas = 'geofas',
  GeofenceTypes = 'geofts',
  GpsFix = 'svfix',
  IgnitionMinimum = 'ig',
  InvestigationTypes = 'invts',
  InvestigationStatuses = 'invsts',
  InteractionTypes = 'its',
  InvestigationId = 'investigationId',
  LocalObjectMaxVelocity = 'lmxv',
  LocalObjectMinVelocity = 'lmnv',
  ObjectTypes = 'objs',
  ObjectTypesRo = 'robjs',
  PartnerResourceFileType = 'pfts',
  Product = 'prods',
  ProductFamily = 'fams',
  RemoteObjectAssetGroups = 'rags',
  RemoteObjectMinVelocity = 'rmnv',
  Scenario = 'scen',
  SoftwareStatus = 'cs',
  StartLattitude = 'sLat',
  StartLongitude = 'sLon',
  TimeOfDayEnd = 'tode',
  TimeOfDayStart = 'tods',
  TimeRange = 'timeRange',
  TimeRangeEnd = 'end',
  TimeRangeStart = 'start',
  TravelledDistanceGreaterThan = 'tdgt',
  Zoom = 'zoom',
}

export type IgnitionMinimumQueryValue =
  | IgnitionMinimum.FifteenMinutes
  | IgnitionMinimum.OneHour
  | null;

export type SoftwareStatusQueryValue = SoftwareStatus.OutOfDate | SoftwareStatus.UpToDate | null;

export interface LocationParams {
  startLat: number;
  startLon: number;
  endLat: number;
  endLon: number;
  zoom: number;
}

export interface CustomTimeRangeParams {
  timeRangeStart: string;
  timeRangeEnd: string;
}

export interface CustomFilterParams extends LocationParams, CustomTimeRangeParams {}

// Extend all filters and make them optional query params.
export interface QueryParams extends FiltersState, CustomFilterParams {
  // The following are additional query params that are not part of the global filters slice.
  assetIds: string; // Comma-separated string of IDs.
  faultLevel: FaultLevel;
  scenario: AssetListScenarios;
  timeRange: TimeRange;
}

export const getUrlParams = <T extends Partial<QueryParams>>({
  assetGroups,
  assetGroupsRo,
  assetIds,
  audible,
  configGroups,
  deviceTypes,
  endLat,
  endLon,
  faultCategories,
  faultLevel,
  faultLevels,
  faults,
  faultStatus,
  geofenceAreas,
  geofenceInteractionTypes,
  geofenceTypes,
  gpsFix,
  ignitionMinimum,
  investigationTypes,
  investigationStatuses,
  maxDistance,
  maxSpeedLo,
  minDuration,
  minSpeedLo,
  minSpeedRo,
  minTravelledDistance,
  objectTypes,
  objectTypesRo,
  partnerResourceTypes,
  partnerResourceFileTypes,
  scenario,
  softwareStatus,
  startLat,
  startLon,
  techLibDocumentTypes,
  techLibProducts,
  techLibProductFamilies,
  timeOfDayEnd,
  timeOfDayStart,
  timeRange,
  timeRangeEnd,
  timeRangeStart,
  vehicleInteractionTypes,
  zoom,
}: T) => {
  const queryParams = new URLSearchParams();

  if (assetGroups?.length) {
    queryParams.append(QueryParamNames.AssetGroups, assetGroups.join(','));
  }

  if (assetGroupsRo?.length) {
    queryParams.append(QueryParamNames.RemoteObjectAssetGroups, assetGroupsRo.join(','));
  }

  if (assetIds?.length) {
    queryParams.append(QueryParamNames.AssetIds, assetIds);
  }

  if (audible && audible !== YesNo.All) {
    // Send 'true' | 'false' string param to the API.
    queryParams.append(QueryParamNames.Audible, Boolean(parseInt(audible)).toString());
  }

  if (configGroups?.length) {
    queryParams.append(QueryParamNames.ConfigGroups, configGroups.join(','));
  }

  if (deviceTypes?.length) {
    queryParams.append(QueryParamNames.DeviceTypes, deviceTypes.join(','));
  }

  if (typeof endLat === 'number') {
    queryParams.append(QueryParamNames.EndLattitude, endLat.toString());
  }

  if (typeof endLon === 'number') {
    queryParams.append(QueryParamNames.EndLongitude, endLon.toString());
  }

  if (faultCategories?.length) {
    queryParams.append(QueryParamNames.FaultCategories, faultCategories.join(','));
  }

  if (faultLevel) {
    queryParams.append(QueryParamNames.FaultLevel, faultLevel);
  }

  if (faultLevels?.length) {
    queryParams.append(QueryParamNames.FaultLevels, faultLevels.join(','));
  }

  if (faults?.length) {
    queryParams.append(QueryParamNames.Faults, faults.join(','));
  }

  if (faultStatus && faultStatus !== FaultStatus.All) {
    queryParams.append(QueryParamNames.FaultStatus, faultStatus);
  }

  if (geofenceAreas?.length) {
    queryParams.append(QueryParamNames.GeofenceAreas, geofenceAreas.join(','));
  }

  if (geofenceTypes?.length) {
    queryParams.append(QueryParamNames.GeofenceTypes, geofenceTypes.join(','));
  }

  if (gpsFix && gpsFix !== GpsFix.All) {
    // Send 'true' | 'false' string param to the API.
    queryParams.append(QueryParamNames.GpsFix, Boolean(parseInt(gpsFix)).toString());
  }

  if (ignitionMinimum && ignitionMinimum !== IgnitionMinimum.All) {
    queryParams.append(QueryParamNames.IgnitionMinimum, ignitionMinimum);
  }

  if (investigationTypes?.length) {
    queryParams.append(QueryParamNames.InvestigationTypes, investigationTypes.join(','));
  }

  if (investigationStatuses?.length) {
    queryParams.append(QueryParamNames.InvestigationStatuses, investigationStatuses.join(','));
  }

  // Both VI and GI share the same api param: 'its'. In theory they should never be needed at the same time.
  if (vehicleInteractionTypes?.length) {
    queryParams.append(QueryParamNames.InteractionTypes, vehicleInteractionTypes.join(','));
  } else if (geofenceInteractionTypes?.length) {
    queryParams.append(QueryParamNames.InteractionTypes, geofenceInteractionTypes.join(','));
  }

  if (typeof maxDistance === 'number') {
    queryParams.append(QueryParamNames.DistanceLessThan, maxDistance.toString());
  }

  if (typeof minDuration === 'number') {
    queryParams.append(QueryParamNames.DurationGreaterThan, minDuration.toString());
  }

  if (typeof maxSpeedLo === 'number') {
    queryParams.append(QueryParamNames.LocalObjectMaxVelocity, maxSpeedLo.toString());
  }

  if (typeof minSpeedLo === 'number') {
    queryParams.append(QueryParamNames.LocalObjectMinVelocity, minSpeedLo.toString());
  }

  if (typeof minSpeedRo === 'number') {
    queryParams.append(QueryParamNames.RemoteObjectMinVelocity, minSpeedRo.toString());
  }

  if (typeof minTravelledDistance === 'number') {
    queryParams.append(
      QueryParamNames.TravelledDistanceGreaterThan,
      minTravelledDistance.toString(),
    );
  }

  if (objectTypes?.length) {
    queryParams.append(QueryParamNames.ObjectTypes, objectTypes.join(','));
  }

  if (objectTypesRo?.length) {
    queryParams.append(QueryParamNames.ObjectTypesRo, objectTypesRo.join(','));
  }

  if (partnerResourceTypes?.length) {
    queryParams.append(QueryParamNames.DocumentType, partnerResourceTypes.join(','));
  }

  if (partnerResourceFileTypes?.length) {
    queryParams.append(QueryParamNames.PartnerResourceFileType, partnerResourceFileTypes.join(','));
  }

  if (scenario) {
    queryParams.append(QueryParamNames.Scenario, scenario);
  }

  if (softwareStatus && softwareStatus !== SoftwareStatus.All) {
    queryParams.append(QueryParamNames.SoftwareStatus, softwareStatus);
  }

  if (typeof startLat === 'number') {
    queryParams.append(QueryParamNames.StartLattitude, startLat.toString());
  }

  if (typeof startLon === 'number') {
    queryParams.append(QueryParamNames.StartLongitude, startLon.toString());
  }

  if (techLibDocumentTypes?.length) {
    queryParams.append(QueryParamNames.DocumentType, techLibDocumentTypes.join(','));
  }

  if (techLibProducts?.length) {
    queryParams.append(QueryParamNames.Product, techLibProducts.join(','));
  }

  if (techLibProductFamilies?.length) {
    queryParams.append(QueryParamNames.ProductFamily, techLibProductFamilies.join(','));
  }

  // The custom time range params must only be sent if they are both defined.
  if (timeRangeStart && timeRangeEnd) {
    queryParams.append(QueryParamNames.TimeRangeStart, timeRangeStart);
    queryParams.append(QueryParamNames.TimeRangeEnd, timeRangeEnd);
  }
  // The timeRange param should not be included if the custom time range params are defined.
  else if (timeRange) {
    queryParams.append(QueryParamNames.TimeRange, timeRange);
  }

  if (timeOfDayEnd) {
    queryParams.append(QueryParamNames.TimeOfDayEnd, timeOfDayEnd);
  }

  if (timeOfDayStart) {
    queryParams.append(QueryParamNames.TimeOfDayStart, timeOfDayStart);
  }

  if (typeof zoom === 'number') {
    queryParams.append(QueryParamNames.Zoom, zoom.toString());
  }

  return queryParams;
};
